import React, { useContext, useEffect, useState } from "react";

import useAiQuota from "../../hooks/ai/useAiQuota";
import { AuthContext } from "../../Contexts/AuthContext";
import "../../Styles/ai-quota-widget.css";

// A floating "AI actions left" badge shown on EVERY screen for a signed-in
// user, so nobody has to open the one screen that happened to show the count.
//
// It is one fixed pill in the corner that toggles a small panel; the open /
// closed choice is remembered per browser. It reads the same useAiQuota the
// pages do, and that hook already updates live on a spend (its own
// AI_QUOTA_SPENT / AI_QUOTA_EXCEEDED listeners), so the number here moves the
// moment any AI action is charged anywhere in the app.
//
// Display only: it never opens the paywall. That belongs to the action the
// user actually took, not to a badge watching from the corner.

const STORAGE_KEY = "aiQuotaWidget:open";

const readOpen = () => {
  try {
    // Default OPEN the first time, so a user discovers it; once they collapse
    // it the choice sticks.
    return window.localStorage.getItem(STORAGE_KEY) !== "closed";
  } catch (error) {
    return true;
  }
};

const writeOpen = (open) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, open ? "open" : "closed");
  } catch (error) {
    // A private window or blocked storage: the widget still works, it just
    // won't remember the choice between loads.
  }
};

const toneFor = (remaining, max) => {
  if (!Number.isFinite(remaining) || !Number.isFinite(max) || max <= 0) {
    return "ok";
  }
  if (remaining <= 0) return "empty";
  // "Low" once a fifth (or 3, whichever is larger) is left, so the warning is
  // meaningful on both the 15-action free tier and the 500-action paid one.
  if (remaining <= Math.max(3, Math.round(max * 0.2))) return "low";
  return "ok";
};

const AiQuotaWidget = () => {
  const { currentUser } = useContext(AuthContext);
  const quota = useAiQuota();
  const [open, setOpen] = useState(readOpen);

  useEffect(() => {
    writeOpen(open);
  }, [open]);

  // Signed out, or the counter has not loaded yet: show nothing rather than a
  // flash of "0 of 0".
  if (!currentUser || !quota.isReady) return null;

  const { isUnlimited, tier, used, max, remaining, resetsAtLabel } = quota;

  const hasFiniteMax = Number.isFinite(max) && max > 0;
  const shownUsed = Math.min(Math.max(Number(used) || 0, 0), hasFiniteMax ? max : Infinity);
  const shownRemaining = hasFiniteMax
    ? Math.max(max - shownUsed, 0)
    : Number(remaining);

  // Nothing sensible to show (no plan limit resolved and not unlimited): stay
  // out of the way rather than render an empty shell.
  if (!isUnlimited && !hasFiniteMax) return null;

  const tone = isUnlimited ? "ok" : toneFor(shownRemaining, max);

  const pillText = isUnlimited
    ? "Unlimited"
    : `${shownRemaining} left`;

  const headingText = isUnlimited
    ? "Unlimited AI access"
    : tier === "paid"
    ? "AI actions this billing period"
    : "Free AI actions";

  return (
    <div
      className={`ai-quota-widget is-${tone}${open ? " is-open" : " is-collapsed"}`}
      data-testid="ai-quota-widget"
    >
      {open ? (
        <div className="ai-quota-widget__panel" role="status" aria-live="polite">
          <button
            type="button"
            className="ai-quota-widget__close"
            aria-label="Hide AI actions"
            onClick={() => setOpen(false)}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              close
            </span>
          </button>

          <div className="ai-quota-widget__heading">
            <span className="material-symbols-outlined ai-quota-widget__spark" aria-hidden="true">
              auto_awesome
            </span>
            {headingText}
          </div>

          {isUnlimited ? (
            <div className="ai-quota-widget__count">You&rsquo;re all set</div>
          ) : (
            <>
              <div className="ai-quota-widget__count">
                <strong>{shownRemaining}</strong> of {max} left
              </div>
              <div
                className="ai-quota-widget__bar"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={max}
                aria-valuenow={shownUsed}
              >
                <span
                  className="ai-quota-widget__bar-fill"
                  style={{ width: `${max > 0 ? (shownUsed / max) * 100 : 0}%` }}
                />
              </div>
              {tier === "paid" && resetsAtLabel ? (
                <div className="ai-quota-widget__meta">Resets {resetsAtLabel}</div>
              ) : (
                <div className="ai-quota-widget__meta">
                  {shownRemaining <= 0
                    ? "You've used your free AI actions"
                    : `${shownUsed} used so far`}
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <button
          type="button"
          className="ai-quota-widget__pill"
          onClick={() => setOpen(true)}
          aria-label={`${pillText} AI actions. Show details.`}
          title="AI actions remaining"
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            auto_awesome
          </span>
          <span className="ai-quota-widget__pill-text">{pillText}</span>
        </button>
      )}
    </div>
  );
};

export default AiQuotaWidget;
